import { useApolloClient, useQuery } from "@apollo/react-hooks";
import { useCallback, useEffect, useState, useRef } from "react";
import {
  GET_DOCTOR_SECRETARY,
  GET_ACTIVE_SPECIALITIES,
  GET_MEDICAL_BY_BRANCH,
  GET_ASSOCIATED_DOCTOR_REQUEST_STATUS,
  USER_AND_MEDICAL,
  VALIDATE_USER_EXIST,
} from "./queries";
import { useToasts } from "react-toast-notifications";
import { isEmpty } from "lodash";

const CEDULA_DOCUMENT_TYPE_ID = 2;
const PASAPORTE_DOCUMENT_TYPE_ID = 4;
const MAX_ASSOCIATED_DOCTOR_TOTAL_FILE_SIZE = 8 * 1024 * 1024;
const ALLOWED_ASSOCIATED_DOCTOR_FILE_TYPES = [
  "application/pdf",
  "image/jpeg",
  "image/png",
];
const ASSOCIATED_DOCTOR_REQUIREMENTS_COUNT = 3;

const getCheckedRequirementsCount = (requirements = {}) =>
  Object.values(requirements).filter(Boolean).length;

const getDocumentsTotalSize = (documents = []) =>
  documents.reduce((total, document) => total + Number(document?.size || 0), 0);

export default function useActions(props) {
  const {
    userSelected,
    branchOfficeId,
    onChange,
    scheduleSelected,
    isNewUser,
    isOpen,
    state,
    isMedical,
    medicalSelected,
    optionProfiles = [],
    branchSelected,
    onAddAndModifyUserMedical,
    onAddAndModifyUser,
  } = props;

  const { addToast } = useToasts();
  const client = useApolloClient();

  const [documentTypeIdSelected, setDocumentTypeIdSelected] = useState(CEDULA_DOCUMENT_TYPE_ID);
  const [identificationInput, setIdentificationInput] = useState("");
  const [isSearchLocked, setIsSearchLocked] = useState(false);
  const [associatedDoctorDocuments, setAssociatedDoctorDocuments] = useState([]);
  const [associatedDoctorRequirements, setAssociatedDoctorRequirements] = useState({});
  const [associatedDoctorSpecialities, setAssociatedDoctorSpecialities] = useState([]);
  const [associatedDoctorSpecialitySelected, setAssociatedDoctorSpecialitySelected] = useState(null);
  const [associatedDoctorSpecialityDocuments, setAssociatedDoctorSpecialityDocuments] = useState({});
  const [otherChannelConfirmation, setOtherChannelConfirmation] = useState(null);
  const [confirmOtherChannelRequest, setConfirmOtherChannelRequest] = useState(false);
  const associatedDoctorDocumentsRef = useRef([]);

  const { data, loading } = useQuery(USER_AND_MEDICAL, {
    skip: !userSelected?.identification,
    fetchPolicy: "network-only",
    variables: {
      where: {
        id: Number(userSelected?.id || 0) || undefined,
        identification: userSelected?.identification,
        documentTypeId: Number(userSelected?.documentTypeId || 0) || undefined,
        branchOfficeId: Number(branchOfficeId || 0) || undefined,
        medicalStaffProviderId: Number(userSelected?.medicalProviderId || 0) || undefined,
      },
    },
  });

  const userAndMedical = data?.userAndMedical || null;

  const isCedulaSelected = documentTypeIdSelected === CEDULA_DOCUMENT_TYPE_ID;
  // Un usuario existente se vincula directamente; la solicitud solo aplica a medicos nuevos.
  const isExistingUser = Boolean(userAndMedical?.id);
  const isExistingUserWithoutMedical = isExistingUser && !userAndMedical?.medicalStaff;
  const isAssociatedDoctorRequest = isNewUser && isMedical === 2 && !isExistingUser;
  const isAssociatedDoctorChecklistCompleted = getCheckedRequirementsCount(associatedDoctorRequirements) === ASSOCIATED_DOCTOR_REQUIREMENTS_COUNT;
  const isSubmitting = Boolean(state?.loadingOrCreateModification);
  const hasSpecialityEvidence = associatedDoctorSpecialities.length > 0 &&
    associatedDoctorSpecialities.every((speciality) => associatedDoctorSpecialityDocuments[speciality.id]);
  const isAddDisabled = isSubmitting || (isAssociatedDoctorRequest &&
    (!isAssociatedDoctorChecklistCompleted || !hasSpecialityEvidence));

  const { data: specialitiesData, loading: loadingSpecialities } = useQuery(
    GET_ACTIVE_SPECIALITIES,
    {
      skip: !isAssociatedDoctorRequest,
      variables: { where: { enabled: true } },
    },
  );
  const associatedDoctorSpecialityOptions = specialitiesData?.specialities || [];

  const formatCedula = useCallback((rawValue) => {
    const digits = String(rawValue || "").replace(/\D/g, "").slice(0, 11);
    if (!digits) return "";
    const firstPart = digits.slice(0, 3);
    const secondPart = digits.slice(3, 10);
    const thirdPart = digits.slice(10, 11);
    let formatted = firstPart;
    if (digits.length > 3) formatted = `${formatted}-${secondPart}`;
    if (digits.length > 10) formatted = `${formatted}-${thirdPart}`;
    return formatted;
  }, []);

  useEffect(() => {
    if (isOpen && isNewUser) {
      const selectedDocumentTypeId = Number(userSelected?.documentTypeId) || CEDULA_DOCUMENT_TYPE_ID;
      setDocumentTypeIdSelected(selectedDocumentTypeId);
      setIsSearchLocked(Boolean(userSelected?.identification));
      const identification = String(userSelected?.identification || "");
      setIdentificationInput(
        selectedDocumentTypeId === CEDULA_DOCUMENT_TYPE_ID
          ? formatCedula(identification)
          : identification.replace(/-/g, ""),
      );
      return;
    }
    setIsSearchLocked(false);
  }, [isOpen, isNewUser, userSelected?.documentTypeId, userSelected?.identification, formatCedula]);

  useEffect(() => {
    associatedDoctorDocumentsRef.current = associatedDoctorDocuments;
  }, [associatedDoctorDocuments]);

  useEffect(() => () => {
    associatedDoctorDocumentsRef.current.forEach((document) => {
      if (document.previewUrl) URL.revokeObjectURL(document.previewUrl);
    });
  }, []);

  const clearAssociatedDoctorDocuments = useCallback(() => {
    setAssociatedDoctorDocuments((currentDocuments) => {
      currentDocuments.forEach((document) => {
        if (document.previewUrl) URL.revokeObjectURL(document.previewUrl);
      });
      return [];
    });
    setAssociatedDoctorRequirements({});
    setAssociatedDoctorSpecialities([]);
    setAssociatedDoctorSpecialitySelected(null);
    setAssociatedDoctorSpecialityDocuments({});
  }, []);

  useEffect(() => {
    if (!isOpen) {
      clearAssociatedDoctorDocuments();
      setDocumentTypeIdSelected(CEDULA_DOCUMENT_TYPE_ID);
      setIdentificationInput("");
      setAssociatedDoctorRequirements({});
      setIsSearchLocked(false);
      return;
    }

    if (isOpen && isNewUser) {
      clearAssociatedDoctorDocuments();
      setDocumentTypeIdSelected(Number(userSelected?.documentTypeId) || CEDULA_DOCUMENT_TYPE_ID);
      setIdentificationInput("");
      setAssociatedDoctorRequirements({});
      setIsSearchLocked(Boolean(userSelected?.identification));
    }
  }, [isOpen, isNewUser, userSelected, clearAssociatedDoctorDocuments]);

  const normalizeIdentificationForSearch = useCallback(() => {
    if (isCedulaSelected) return identificationInput.replace(/\D/g, "").slice(0, 11);
    return identificationInput.trim();
  }, [identificationInput, isCedulaSelected]);

  const normalizeIdentificationForComparison = (value, documentTypeId) =>
    Number(documentTypeId) === CEDULA_DOCUMENT_TYPE_ID
      ? String(value || "").replace(/\D/g, "")
      : String(value || "").trim().toUpperCase();

  const validateUserLocally = (identification) =>
    state.some(
      (item) =>
        Number(item?.documentTypeId) === Number(documentTypeIdSelected) &&
        normalizeIdentificationForComparison(
          item?.identification,
          item?.documentTypeId,
        ) ===
          normalizeIdentificationForComparison(
            identification,
            documentTypeIdSelected,
          ),
    );

  const onSearchUser = () => {
    const identification = normalizeIdentificationForSearch();
    if (!identification) {
      return addToast("Completar campo Documento", { appearance: "error", autoDismiss: true });
    }
    if (isCedulaSelected && identification.length !== 11) {
      return addToast("Completar campo Cedula", { appearance: "error", autoDismiss: true });
    }
    if (validateUserLocally(identification)) {
      return addToast("El Usuario ya esta registrado", { appearance: "error", autoDismiss: true });
    }

    const completeSearch = (rejected = null) => {
      const requestedProfileId = Number(rejected?.requestedProfileId || 0);
      const requestedProfile = optionProfiles.find(
        (profile) =>
          Number(profile?.id) === requestedProfileId &&
          profile?.enabled !== false &&
          Boolean(profile?.emitsRecipe),
      );

      onChange("userSelected", { identification, documentTypeId: documentTypeIdSelected });
      onChange("medicalSelected", rejected
        ? {
            ...rejected,
            identificationCard: rejected.identificationCard,
            preregistrationId: rejected.id,
            profileId: requestedProfileId || null,
          }
        : { identificationCard: identification, documentTypeId: documentTypeIdSelected });
      if (rejected) {
        onChange("isMedical", 2);
        onChange("profileSelected", requestedProfile || null);
        onChange("scheduleSelected", rejected.medicalSchedule || []);
        setAssociatedDoctorRequirements(
          (rejected.documentRequirements || []).reduce(
            (result, requirement) => ({ ...result, [requirement.key]: Boolean(requirement.checked) }),
            {},
          ),
        );
        setAssociatedDoctorSpecialities(rejected.specialities || []);
        setAssociatedDoctorSpecialitySelected(null);
        addToast(
          `Solicitud rechazada: ${rejected.rejectionReason || "sin motivo registrado"}. Corrija los datos y reenvíela.`,
          { appearance: "error", autoDismiss: true },
        );
      } else {
        onChange("scheduleSelected", []);
      }
      setIsSearchLocked(true);
    };

    const showSearchError = (message) =>
      addToast(message, { appearance: "error", autoDismiss: true });
    const providerId = Number(branchSelected?.pharmacyProviderId || 0);

    // Una sucursal sin guardar aun no tiene solicitudes que validar.
    if (!providerId || !Number(branchOfficeId || 0)) {
      return completeSearch();
    }

    client.query({
      query: GET_ASSOCIATED_DOCTOR_REQUEST_STATUS,
      fetchPolicy: "network-only",
      variables: {
        where: {
          documentType: isCedulaSelected ? "CEDULA" : "PASAPORTE",
          identificationCard: identification,
          providerId,
          branchOfficeId: Number(branchOfficeId || 0),
        },
      },
    }).then(({ data }) => {
      const requestStatus = data?.associatedDoctorRequestStatus || {};

      // Este consultorio ya tiene una solicitud pendiente o rechazada sin derecho a correccion.
      if (requestStatus.sameChannelStatus && !requestStatus.canResubmit) {
        return showSearchError(requestStatus.message);
      }

      if (requestStatus.canResubmit) {
        return completeSearch(requestStatus.rejectedPreregistration);
      }

      if (requestStatus.hasOtherChannelRequest) {
        return setOtherChannelConfirmation({
          message: requestStatus.message,
          onConfirm: () => {
            setConfirmOtherChannelRequest(true);
            completeSearch();
          },
        });
      }

      return completeSearch();
    }).catch((searchError) =>
      showSearchError(
        searchError?.graphQLErrors?.[0]?.message ||
          "No fue posible validar las solicitudes del médico.",
      ),
    );
  };

  const confirmOtherChannelSearch = () => {
    const onConfirm = otherChannelConfirmation?.onConfirm;
    setOtherChannelConfirmation(null);
    if (onConfirm) onConfirm();
  };

  const cancelOtherChannelSearch = () => {
    setOtherChannelConfirmation(null);
  };

  const onResetModalSearch = () => {
    setConfirmOtherChannelRequest(false);
    setDocumentTypeIdSelected(CEDULA_DOCUMENT_TYPE_ID);
    setIdentificationInput("");
    setIsSearchLocked(false);
    clearAssociatedDoctorDocuments();
    onChange("userSelected", null);
    onChange("medicalSelected", null);
    onChange("profileSelected", null);
    onChange("isMedical", 0);
    onChange("schedulingFormat", 0);
    onChange("scheduleSelected", []);
  };

  const onChangeDocumentType = (typeId) => {
    setDocumentTypeIdSelected(typeId);
    setIdentificationInput((previousValue) => {
      if (typeId === PASAPORTE_DOCUMENT_TYPE_ID) return String(previousValue || "").replace(/-/g, "");
      return formatCedula(previousValue);
    });
  };

  const onAssociatedDoctorRequirementFileChange = (requirementKey, { target }) => {
    const file = target?.files?.[0];
    target.value = "";
    if (!file) return;
    if (!ALLOWED_ASSOCIATED_DOCTOR_FILE_TYPES.includes(file.type)) {
      return addToast("Solo se admiten documentos PDF, JPG o PNG.", {
        appearance: "error",
        autoDismiss: true,
      });
    }

    const otherDocuments = associatedDoctorDocuments.filter(
      (document) => document.requirementKey !== requirementKey,
    );
    if (
      getDocumentsTotalSize([
        ...otherDocuments,
        ...Object.values(associatedDoctorSpecialityDocuments),
        file,
      ]) > MAX_ASSOCIATED_DOCTOR_TOTAL_FILE_SIZE
    ) {
      return addToast("El total de documentos no puede superar 8 MB.", {
        appearance: "error",
        autoDismiss: true,
      });
    }

    setAssociatedDoctorDocuments((currentDocuments) => {
      const replacedDocument = currentDocuments.find(
        (document) => document.requirementKey === requirementKey,
      );
      if (replacedDocument?.previewUrl) {
        URL.revokeObjectURL(replacedDocument.previewUrl);
      }
      return [
        ...currentDocuments.filter(
          (document) => document.requirementKey !== requirementKey,
        ),
        {
          id: `${requirementKey}-${file.name}-${file.size}-${file.lastModified}`,
          name: file.name,
          size: file.size,
          type: file.type,
          file,
          requirementKey,
          previewUrl: file.type.startsWith("image/")
            ? URL.createObjectURL(file)
            : "",
        },
      ];
    });
    setAssociatedDoctorRequirements((currentRequirements) => ({
      ...currentRequirements,
      [requirementKey]: true,
    }));
  };

  const onRemoveAssociatedDoctorDocument = (documentId) => {
    setAssociatedDoctorDocuments((currentDocuments) => {
      const documentToRemove = currentDocuments.find((item) => item.id === documentId);
      if (documentToRemove?.previewUrl) URL.revokeObjectURL(documentToRemove.previewUrl);
      if (documentToRemove?.requirementKey) {
        setAssociatedDoctorRequirements((currentRequirements) => ({
          ...currentRequirements,
          [documentToRemove.requirementKey]: false,
        }));
      }
      return currentDocuments.filter((item) => item.id !== documentId);
    });
  };

  const onAddAssociatedDoctorSpeciality = () => {
    if (!associatedDoctorSpecialitySelected) return;
    const specialityId = Number(associatedDoctorSpecialitySelected.id);
    if (
      associatedDoctorSpecialities.some(
        (speciality) => Number(speciality.id) === specialityId,
      )
    ) {
      setAssociatedDoctorSpecialitySelected(null);
      return;
    }
    setAssociatedDoctorSpecialities((currentSpecialities) => [
      ...currentSpecialities,
      associatedDoctorSpecialitySelected,
    ]);
    setAssociatedDoctorSpecialitySelected(null);
  };

  const onRemoveAssociatedDoctorSpeciality = (specialityId) => {
    setAssociatedDoctorSpecialities((currentSpecialities) =>
      currentSpecialities.filter(
        (speciality) => Number(speciality.id) !== Number(specialityId),
      ),
    );
    setAssociatedDoctorSpecialityDocuments((currentDocuments) => {
      const document = currentDocuments[specialityId];
      if (document?.previewUrl) URL.revokeObjectURL(document.previewUrl);
      const nextDocuments = { ...currentDocuments };
      delete nextDocuments[specialityId];
      return nextDocuments;
    });
  };

  const onAssociatedDoctorSpecialityFileChange = (specialityId, { target }) => {
    const file = target?.files?.[0];
    target.value = "";
    if (!file) return;
    if (!ALLOWED_ASSOCIATED_DOCTOR_FILE_TYPES.includes(file.type)) {
      return addToast("Solo se admiten documentos PDF, JPG o PNG.", { appearance: "error", autoDismiss: true });
    }
    const otherDocuments = Object.entries(associatedDoctorSpecialityDocuments)
      .filter(([id]) => Number(id) !== Number(specialityId))
      .map(([, document]) => document);
    if (getDocumentsTotalSize([...associatedDoctorDocuments, ...otherDocuments, file]) > MAX_ASSOCIATED_DOCTOR_TOTAL_FILE_SIZE) {
      return addToast("El total de documentos no puede superar 8 MB.", { appearance: "error", autoDismiss: true });
    }
    setAssociatedDoctorSpecialityDocuments((currentDocuments) => {
      if (currentDocuments[specialityId]?.previewUrl) URL.revokeObjectURL(currentDocuments[specialityId].previewUrl);
      return {
        ...currentDocuments,
        [specialityId]: {
          id: `speciality-${specialityId}-${file.name}-${file.lastModified}`,
          name: file.name,
          size: file.size,
          type: file.type,
          file,
          specialityId: Number(specialityId),
          requirementKey: "specialtyDegreeMsp",
          previewUrl: file.type.startsWith("image/") ? URL.createObjectURL(file) : "",
        },
      };
    });
  };

  const handleSubmit = async () => {
    const idUser = medicalSelected?.userId || userSelected?.id;
    const username = medicalSelected?.username || userSelected?.username;
    const email = medicalSelected?.email || userSelected?.email;
    onChange("loadingOrCreateModification", true);
    onChange("isLoadingDhn", true);

    if (await validateUserExist(idUser, username, email)) {
      onChange("loadingOrCreateModification", false);
      onChange("isLoadingDhn", false);
      return;
    }

    let operationCompleted;
    if (isMedical === 2) {
      const skipPrereq = Boolean(userAndMedical);
      operationCompleted = await onAddAndModifyUserMedical(
        {
          documents: [
            ...associatedDoctorDocuments,
            ...Object.values(associatedDoctorSpecialityDocuments),
          ],
          checkedRequirements: associatedDoctorRequirements,
          specialities: associatedDoctorSpecialities,
          confirmOtherChannelRequest,
        },
        { skipPrereq },
      );
    } else {
      operationCompleted = await onAddAndModifyUser();
    }

    if (operationCompleted && isAssociatedDoctorRequest) {
      clearAssociatedDoctorDocuments();
    } else if (operationCompleted) {
      clearAssociatedDoctorDocuments();
      setDocumentTypeIdSelected(CEDULA_DOCUMENT_TYPE_ID);
      setIdentificationInput("");
      setAssociatedDoctorRequirements({});
      setIsSearchLocked(false);
    }

    onChange("loadingOrCreateModification", false);
    onChange("isLoadingDhn", false);
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const optionDaysHours = [
    { weekDay: 1, label: "Lunes" },
    { weekDay: 2, label: "Martes" },
    { weekDay: 3, label: "Miércoles" },
    { weekDay: 4, label: "Jueves" },
    { weekDay: 5, label: "Viernes" },
    { weekDay: 6, label: "Sábado" },
    { weekDay: 7, label: "Domingo" },
  ];


  const { data: { getMedicalByBranch } = [] } = useQuery(
    GET_MEDICAL_BY_BRANCH,
    {
      fetchPolicy: "network-only",
      variables: {
        where: {
          branchOfficeId,
        },
      },
    }
  );

  const { data: { getDoctorSecretary } = [], loading: secretaryDoctorLoading } =
    useQuery(GET_DOCTOR_SECRETARY, {
      skip: !userSelected?.id || !isEmpty(userSelected?.doctorSecretary),
      fetchPolicy: "network-only",
      variables: {
        where: { userId: userSelected?.id, branchOfficeId },
      },
    });

  const validationAtleastOneDoctorActive = useCallback(() => {
    if (
      userSelected?.doctorSecretary?.filter(
        (item) => item?.enabled && !item?.wasEliminated
      )?.length === 1
    ) {
      addToast("Debe tener al menos un Medico Activo", {
        appearance: "error",
        autoDismiss: true,
      });
      return true;
    }
    return false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addToast, userSelected?.doctorSecretary]);

  const validationAtLeastOneDoctor = useCallback(() => {
    if (
      userSelected?.doctorSecretary?.filter(
        (item) => !item?.wasEliminated && item?.enabled
      ) === 1
    ) {
      addToast("Debe tener al menos un medico asignado", {
        appearance: "error",
        autoDismiss: true,
      });
      return true;
    }
    return false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addToast, userSelected?.doctorSecretary?.length]);

  const validateUserExist = async (idUser, username, email) => {
    try {
      const { data } = await client.query({
        query: VALIDATE_USER_EXIST,
        fetchPolicy: "network-only",
        variables: {
          where: {
            id: idUser || null,
            username,
            email,
          },
        },
      });
      const validateUser = data?.validateUser;
      return Boolean(validateUser);
    } catch (error) {
      addToast(error.message, { appearance: "error", autoDismiss: true });
      return true;
    }
  };

  useEffect(() => {
    if (!isEmpty(getDoctorSecretary) && !secretaryDoctorLoading) {
      onChange("doctorSecretary", getDoctorSecretary, "userSelected");
    }
  }, [getDoctorSecretary, onChange, secretaryDoctorLoading]);

  useEffect(() => {
    if (
      userSelected?.edit ||
      (Array.isArray(scheduleSelected) && scheduleSelected.length > 0)
    )
      return;

    if (
      userAndMedical &&
      !loading &&
      (userSelected?.id || userSelected?.identification)
    ) {
      const {
        profiles,
        medicalStaff,
        specialities,
        medicalSchedule,
        averageAppoimentTime,
        ...rest
      } = userAndMedical ?? {};
      onChange("profileSelected", profiles);
      const hasMedicalStaff = Boolean(medicalStaff);
      onChange("isMedical", hasMedicalStaff ? 2 : 1);
      if (hasMedicalStaff) {
        onChange("medicalSelected", {
          ...medicalStaff,
          specialities,
          averageAppoimentTime,
        });
      }
      if (isNewUser) {
        onChange("userSelected", rest);
      }
      if (!hasMedicalStaff) {
        onChange("scheduleSelected", optionDaysHours);
      }
      if (hasMedicalStaff) {
        if (
          averageAppoimentTime !== null &&
          averageAppoimentTime !== undefined &&
          averageAppoimentTime >= 0
        ) {
          onChange(
            "schedulingFormat",
            Number(averageAppoimentTime) > 0 ? 2 : 1
          );
        } else {
          onChange("schedulingFormat", 0);
        }
        const updatedSchedule = optionDaysHours.map((item) => {
          const dataItem2 = medicalSchedule?.find(
            (item2) => item2.weekDay === item.weekDay
          );
          return dataItem2
            ? {
                enabled: dataItem2.enabled,
                fromHour: dataItem2.fromHour,
                id: dataItem2?.id,
                toHour: dataItem2.toHour,
                weekDay: dataItem2.weekDay,
                label: item.label,
              }
            : item;
        });
        onChange("scheduleSelected", updatedSchedule);
      }
    } else if (!loading) {
      onChange("scheduleSelected", optionDaysHours);
    }
  }, [
    branchOfficeId,
    isNewUser,
    loading,
    onChange,
    optionDaysHours,
    scheduleSelected,
    userAndMedical,
    userSelected,
  ]);

  return {
    state: {
      documentTypeIdSelected,
      identificationInput,
      isSearchLocked,
      associatedDoctorDocuments,
      associatedDoctorRequirements,
      associatedDoctorSpecialities,
      associatedDoctorSpecialitySelected,
      associatedDoctorSpecialityDocuments,
      associatedDoctorSpecialityOptions,
      isCedulaSelected,
      isAssociatedDoctorRequest,
      isAssociatedDoctorChecklistCompleted,
      isSubmitting,
      isAddDisabled,
      loading: loading || loadingSpecialities,
      userAndMedical,
      isExistingUserWithoutMedical,
      otherChannelConfirmation,
      CEDULA_DOCUMENT_TYPE_ID,
      PASAPORTE_DOCUMENT_TYPE_ID
    },
    actions: {
      onChangeDocumentType,
      onAssociatedDoctorRequirementFileChange,
      onRemoveAssociatedDoctorDocument,
      onAssociatedDoctorSpecialitySelectedChange: setAssociatedDoctorSpecialitySelected,
      onAddAssociatedDoctorSpeciality,
      onRemoveAssociatedDoctorSpeciality,
      onAssociatedDoctorSpecialityFileChange,
      onSearchUser,
      confirmOtherChannelSearch,
      cancelOtherChannelSearch,
      onResetModalSearch,
      clearAssociatedDoctorDocuments,
      validateUserExist,
      getMedicalByBranch,
      validationAtLeastOneDoctor,
      validationAtleastOneDoctorActive,
      handleSubmit,
      setIdentificationInput
    }
  };
}
